import { useMemo } from 'react'
import { buildForm, type TableQuickCreateSpec } from '@solum/experience'
import type { Row, SchemaDescription, SemanticDescription } from '@solum/sdk'
import { Button } from '@solum/ui'
import { EntityForm } from '../form/EntityForm'
import { WriteFailureNotice } from '../data/WriteFailure'
import type { TablePresentationFormChoice } from './useTablePresentationSettings'
import { RefPicker, refTarget } from '../form/RefPicker'

/**
 * The declared quick-create recipe, rendered as a FORM (#2406 ruling 79217).
 *
 * Form identity is semantic and portable; the current deployment row supplies
 * the sections, while the table document supplies the strict field subset. The
 * ruling is explicit that this survives — "a governed quick-create field subset
 * may configure the initial create form; it is a form presentation, not an
 * inline row" — so what changed is only WHERE it renders and who tells it to
 * close.
 *
 * It used to take a `GhostCreateContext`, which coupled a governed form to the
 * grid's provisional-row ceremony. Two plain callbacks instead: the host owns
 * the surface, and this owns the form.
 */
export function GovernedQuickCreate({
  entity, schema, semantics, spec, forms, save, onClose, onComplete, onCreated,
}: {
  entity: string
  schema: SchemaDescription
  semantics: SemanticDescription
  spec: TableQuickCreateSpec
  forms: readonly TablePresentationFormChoice[]
  save: { mutateAsync: (value: { id: string; patch: Row }) => Promise<unknown>; isPending: boolean; error?: unknown }
  /** Close the surface, writing nothing. */
  onClose: () => void
  /** The write succeeded; the host announces and refreshes. */
  onComplete: (announcement?: string) => void
  onCreated?: (id: string) => void
}) {
  const form = spec.form ? forms.find((candidate) => candidate.logical_key === spec.form) : forms[0]
  const view = useMemo(() => {
    const built = buildForm(schema, semantics, entity, form ? { sections: form.sections } : undefined)
    const fields = new Set(spec.fields)
    return {
      ...built,
      sections: built.sections
        .map((section) => ({ ...section, fields: section.fields.filter((field) => fields.has(field.field)) }))
        .filter((section) => section.fields.length > 0),
    }
  }, [entity, form, schema, semantics, spec.fields])

  return <>
    <EntityForm
      view={view}
      row={{}}
      isNew
      saving={save.isPending}
      onSave={(patch) => save.mutateAsync({ id: 'new', patch })}
      refPicker={(field, value, onChange, values) => {
        const resolution = refTarget(schema, entity, field.field)
        const discriminatorColumn = resolution.kind === 'none' ? null : resolution.discriminatorColumn
        const discriminatorValue = discriminatorColumn && values[discriminatorColumn] != null
          ? String(values[discriminatorColumn]) : null
        return <RefPicker resolution={resolution} column={field.field} value={value}
          discriminatorValue={discriminatorValue} onChange={onChange}
          disabled={save.isPending} testid={`quick-ref-${field.field}`} />
      }}
      afterSave={(result) => {
        const id = String((result as Row | undefined)?.id ?? '')
        onComplete(`${view.title} created`)
        if (id) onCreated?.(id)
      }}
      notice={save.error ? <WriteFailureNotice error={save.error} what={`this ${view.title.toLowerCase()}`} /> : undefined}
    />
    <Button type="button" variant="ghost" data-testid="governed-quick-create-cancel"
      disabled={save.isPending} onClick={onClose}>Cancel</Button>
  </>
}
